import { statusKind, STATUS_LOOK, cellNumber, heatmapColumns } from './dashboardTheme.js'

/**
 * Heatmap — a table's grid, coloured, so the pattern is seen before any cell is read.
 *
 *   - A column of figures is shaded light to dark in the theme's one hue. Each column is shaded
 *     against itself, because columns are different measures (people, dollars, percent) and one
 *     scale across them would let the biggest unit drown the rest; the note under the grid says so.
 *   - A column of status words (Ready / Partly / Missing) fills each cell with that status's colour,
 *     and the word stays printed in it — the colour adds to the word, never replaces it. Under each
 *     such column, how many of its cells are good.
 *   - Any other column (the names) is printed as it is.
 *
 * The values are always printed; the colour is what makes the gaps and the outliers jump out.
 */
const readable = (i) => (i >= 2 ? '#ffffff' : '#1A1A1A')

export default function Heatmap({ columns, rows, theme }) {
  const kinds = heatmapColumns(columns, rows)
  const ramp = theme.ramp
  const shadeIdx = (k, n) => {
    if (k.max === k.min) return 2
    return Math.min(ramp.length - 1, Math.floor(((n - k.min) / (k.max - k.min)) * ramp.length))
  }
  const anyNumber = kinds.some(k => k.kind === 'number')

  return (
    <div>
      <div className="overflow-x-auto rounded-2xl border border-gray-200 bg-white shadow-xs">
        <table className="w-full text-xs border-separate" style={{ borderSpacing: 2 }}>
          {Array.isArray(columns) && columns.length > 0 && (
            <thead>
              <tr>
                {columns.map((c, i) => (
                  <th key={i} className={`px-2.5 py-2 text-[11px] uppercase tracking-wide font-bold text-[var(--pt-accent-dark)] ${kinds[i]?.kind === 'text' ? 'text-left' : 'text-center'}`}>{String(c)}</th>
                ))}
              </tr>
            </thead>
          )}
          <tbody>
            {rows.map((row, ri) => (
              <tr key={ri}>
                {(Array.isArray(row) ? row : [row]).map((cell, ci) => {
                  const k = kinds[ci] || { kind: 'text' }
                  const text = cell === null || cell === undefined || String(cell).trim() === '' ? '—' : String(cell)
                  if (k.kind === 'status') {
                    const st = statusKind(cell)
                    const look = st ? STATUS_LOOK[st] : null
                    return (
                      <td key={ci} title={text} className="px-2 py-2 text-center font-semibold rounded-md"
                          style={{ background: look ? look.bg : '#F7F6F3', color: look ? look.fg : '#9CA3AF' }}>
                        <span className="inline-flex items-center gap-1.5">
                          {look && <span className="w-1.5 h-1.5 rounded-full" style={{ background: look.dot }} aria-hidden="true" />}
                          {text}
                        </span>
                      </td>
                    )
                  }
                  if (k.kind === 'number') {
                    const n = cellNumber(cell)
                    if (n === null) return <td key={ci} className="px-2 py-2 text-center text-gray-400 rounded-md bg-[#F7F6F3]">—</td>
                    const i = shadeIdx(k, n)
                    return (
                      <td key={ci} title={`${String(columns?.[ci] ?? '')}: ${text}`}
                          className="px-2 py-2 text-center tabular-nums font-semibold rounded-md"
                          style={{ background: ramp[i], color: readable(i), transition: 'background .4s ease' }}>{text}</td>
                    )
                  }
                  return <td key={ci} className={`px-2.5 py-2 text-gray-800 font-medium ${text.length > 28 ? 'min-w-[12rem]' : 'whitespace-nowrap'}`}>{text}</td>
                })}
              </tr>
            ))}
          </tbody>
          {kinds.some(k => k.kind === 'status') && (
            <tfoot>
              <tr>
                {kinds.map((k, i) => {
                  if (k.kind !== 'status') return <td key={i} className="px-2.5 pt-2 text-[11px] text-gray-500">{i === 0 ? 'Complete' : ''}</td>
                  const cells = rows.map(r => (Array.isArray(r) ? r[k.c] : null))
                  const good = cells.filter(v => statusKind(v) === 'good').length
                  const pct = rows.length ? Math.round((good / rows.length) * 100) : 0
                  return (
                    <td key={i} className="px-2 pt-2 text-center">
                      <span className="block text-[11px] font-bold tabular-nums text-gray-800">{good}/{rows.length}</span>
                      <span className="mt-1 block h-1.5 rounded-full bg-gray-100 overflow-hidden" aria-hidden="true">
                        <span className="block h-full rounded-full pt-grow" style={{ width: `${pct}%`, background: STATUS_LOOK.good.dot }} />
                      </span>
                    </td>
                  )
                })}
              </tr>
            </tfoot>
          )}
        </table>
      </div>
      {anyNumber && (
        <div className="mt-2 flex flex-wrap items-center gap-2 text-[11px] text-gray-500">
          <span>Lower</span>
          {ramp.map((c, i) => <span key={i} className="w-5 h-2.5 rounded-sm" style={{ background: c }} aria-hidden="true" />)}
          <span>Higher</span>
          <span className="text-gray-400">· each column is shaded against itself</span>
        </div>
      )}
    </div>
  )
}
